import React, { useEffect, useState, useRef, useMemo } from "react";
import { Award, Profile, Book, AddCircle, TrendUp, Calendar, ArrowRight2 } from "iconsax-reactjs";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { fetchClassRooms } from "../../../redux/slices/classRoomSlice";
import { fetchUserEnrolledCohorts } from "../../../redux/slices/userEnrolledCohortSlice";
import { fetchAssignments } from "../../../redux/slices/assignmentSlice";
import { useDispatch, useSelector } from "react-redux";
import { fetchRecapMaterials, fetchRecapMaterialByCohortSlug, } from "../../../redux/slices/classRecapMaterialSlice";
import { useFetchUser } from "../../../utils/useUserStore";
import FileInput from "../../../components/individual/FileInput";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import cohortImage from "../../../assets/image/background/background.jpeg";
import { fetchAnalyticsData } from "../../../redux/slices/analyticsSlice";
import { fetchUserProgress } from "../../../redux/slices/progressSlice";
import api from "../../../utils/api.js";


import { deduplicateEnrollments } from "../../../utils/enrollmentUtils";

function MemberOrganizationDashboard() {
  // const { user } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useFetchUser();
  const [loading, setLoading] = useState(true);
  const [libraryData, setLibraryData] = useState([]);
  const [error, setError] = useState(null);
  const nextClassRooms = useSelector((state) => state.classRooms.classRooms);
  const classRoomsLoading = useSelector((state) => state.classRooms.loading);
  const userAssignments = useSelector((state) => state.userAssignments.assignments);
  const recapMaterials = useSelector((state) => state.classRecapMaterials.recapMaterials);
  const userEnrolledCohorts = useSelector((state) => state.userEnrolledCohorts.enrollment);
  const userEnrolledLoading = useSelector((state) => state.userEnrolledCohorts.loading);
  const userAnalytics = useSelector((state) => state.userAnalytics.analyticsData);
  // Progress data from new API
  const { progressData, loading: progressLoading } = useSelector((state) => state.progress);

  // Combine and deduplicate userEnrolledCohorts and progressData to ensure accurate enrolled courses
  const enrollments = useMemo(() => {
    return deduplicateEnrollments(userEnrolledCohorts, progressData);
  }, [userEnrolledCohorts, progressData]);

  const overallProgress = useMemo(() => {
    if (enrollments.length > 0) {
      const sum = enrollments.reduce((acc, curr) => acc + (Number(curr.progress) || 0), 0);
      return Math.round(sum / enrollments.length);
    }
    return Number(userAnalytics?.overall_completion) || 0;
  }, [enrollments, userAnalytics]);

  const completedAssignmentsCount = useMemo(() => {
    if (enrollments.length > 0) {
      return enrollments.reduce((acc, curr) => acc + (curr.breakdown?.completed_assignments || 0), 0);
    }
    return 0;
  }, [enrollments]);

  const releasedAssignmentsCount = useMemo(() => {
    if (enrollments.length > 0) {
      return enrollments.reduce((acc, curr) => acc + (curr.breakdown?.released_assignments || 0), 0);
    }
    return 0;
  }, [enrollments]);

  const [selectedCohortSlug, setSelectedCohortSlug] = useState(null);

  useEffect(() => {
    if (!selectedCohortSlug && enrollments.length > 0) {
      const firstWithSlug = enrollments.find((e) => e.cohort?.slug);
      if (firstWithSlug?.cohort?.slug) {
        setSelectedCohortSlug(firstWithSlug.cohort.slug);
      }
    }
  }, [enrollments, selectedCohortSlug]);

  const canJoin = nextClassRooms?.next_class?.can_join;
  const lastFetchedSlug = useRef();
  const lastFetchedCohortId = useRef();

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchUserEnrolledCohorts());
    dispatch(fetchAssignments());
    dispatch(fetchAnalyticsData());
    dispatch(fetchUserProgress());
    handleLibrary();
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchRecapMaterials());
  }, [dispatch]);

  useEffect(() => {
    if (selectedCohortSlug && lastFetchedSlug.current !== selectedCohortSlug) {
      dispatch(fetchRecapMaterialByCohortSlug(selectedCohortSlug));
      lastFetchedSlug.current = selectedCohortSlug;
    }
  }, [dispatch, selectedCohortSlug]);

  useEffect(() => {
    if (!selectedCohortSlug) return;
    if (lastFetchedCohortId.current === selectedCohortSlug) return;

    dispatch(fetchClassRooms(selectedCohortSlug));
    lastFetchedCohortId.current = selectedCohortSlug;
  }, [dispatch, selectedCohortSlug]);

  const isLoading = classRoomsLoading || userEnrolledLoading;

  const handleJoinClass = (slug) => {
    const targetSlug = slug || selectedCohortSlug;
    if (targetSlug && canJoin) {
      navigate(`/organization/classroom/${targetSlug}`);
    }
  };

  // Helper function to format date as d m y
  const formatDateDMY = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  // Helper function to determine resource type from file_path and title
  const getResourceType = (filePath, title = "") => {
    const combined = `${filePath || ""} ${title || ""}`.toLowerCase();
    if (combined.includes(".ipynb") || combined.includes("notebook") || combined.includes("colab")) {
      return { type: "notebook", label: "Notebook", icon: "📓", pillClass: "material-pill-notebook" };
    }
    if (combined.includes("zoom.us") || combined.includes("recording") || combined.includes(".mp4") || combined.includes(".webm") || combined.includes("video")) {
      return { type: "video", label: "Recording", icon: "🎥", pillClass: "material-pill-video" };
    }
    if (combined.includes("youtube.com") || combined.includes("youtu.be")) {
      return { type: "youtube", label: "YouTube", icon: "▶️", pillClass: "material-pill-video" };
    }
    if (combined.includes("drive.google.com") || combined.includes("docs.google.com")) {
      return { type: "drive", label: "Drive", icon: "📁", pillClass: "material-pill-drive" };
    }
    if (combined.includes(".pdf") || combined.includes(".docx") || combined.includes(".pptx") || combined.includes("slide") || combined.includes("document")) {
      return { type: "document", label: "Document", icon: "📄", pillClass: "material-pill-document" };
    }
    return { type: "file", label: "Resource", icon: "🔗", pillClass: "material-pill-default" };
  };

  // Group materials by week_number for dashboard display
  const groupedMaterials = useMemo(() => {
    if (!recapMaterials || recapMaterials.length === 0) return {};

    const groups = {};
    recapMaterials.forEach((material) => {
      const weekNum = material.week_number || "general";
      if (!groups[weekNum]) {
        groups[weekNum] = [];
      }
      groups[weekNum].push(material);
    });

    // Sort weeks in descending order, but "general" goes last
    const sortedWeeks = Object.keys(groups).sort((a, b) => {
      if (a === "general") return 1;
      if (b === "general") return -1;
      return Number(b) - Number(a);
    });

    const sortedGroups = {};
    sortedWeeks.forEach(week => {
      sortedGroups[week] = groups[week];
    });

    return sortedGroups;
  }, [recapMaterials]);

  const weeks = Object.keys(groupedMaterials).sort((a, b) => {
    if (a === "general") return 1;
    if (b === "general") return -1;
    return Number(b) - Number(a);
  });

  // Get display label for week
  const getWeekLabel = (week) => {
    if (week === "general") return "General";
    return `Week ${week}`;
  };

  // Get materials to display (top 3 from first week)
  const displayMaterials = useMemo(() => {
    if (!recapMaterials || recapMaterials.length === 0) return [];

    // Get the first week's materials
    const firstWeek = weeks[0];
    if (!firstWeek) return [];

    return groupedMaterials[firstWeek].slice(0, 3);
  }, [groupedMaterials, weeks, recapMaterials]);

  const handleLibrary = async () => {
    try {
      setLoading(true);
      const response = await api.get("/user/libraries");
      const data = response.data.data;
      setLibraryData(data);
    } catch (error) {
      // console.error(error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="organization-dashboard-main-section">
      <div className="container p-4 min-vh-100">
        {/* Welcome Section */}
        <section className=" mb-4">
          <div className="welcome-head">
            <h1 className="welcome-text-title">
              Welcome back, {user?.name || "Learner"}!
            </h1>
            <p className="text-muted">Continue your learning journey</p>
          </div>
        </section>
        <div className="row mb-4">
          <div className="col-md-4 mb-3">
            <Link to="/organization/my-courses" className="text-decoration-none text-dark">
              <div className="dash-stat-card h-100" style={{ cursor: "pointer" }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="stat-title">My Courses</span>
                  <div className="stat-icon-wrapper stat-icon-blue">
                    <Book size="22" color="#2563eb" />
                  </div>
                </div>
                <h2 className="stat-value">
                  {enrollments.length > 0 ? enrollments.length : (userAnalytics?.active_courses_count || 0)}
                </h2>
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <p className="stat-subtitle">Enrolled courses</p>
                  <span className="text-primary small fw-semibold d-flex align-items-center">
                    View all <ArrowRight2 size={12} className="ms-1" />
                  </span>
                </div>
              </div>
            </Link>
          </div>
          <div className="col-md-4 mb-3">
            <Link to="/organization/progress" className="text-decoration-none text-dark">
              <div className="dash-stat-card h-100" style={{ cursor: "pointer" }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="stat-title">Progress</span>
                  <div className="stat-icon-wrapper stat-icon-emerald">
                    <TrendUp size="22" color="#059669" />
                  </div>
                </div>
                {progressLoading && enrollments.length === 0 ? (
                  <Skeleton width={80} height={36} />
                ) : (
                  <>
                    <h2 className="stat-value">{overallProgress}%</h2>
                    <div className="modern-progress-track my-2" style={{ height: "6px" }}>
                      <div
                        className="modern-progress-bar"
                        style={{
                          width: `${overallProgress}%`,
                          background: "linear-gradient(90deg, #10b981, #059669)",
                        }}
                      />
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <p className="stat-subtitle">
                        {releasedAssignmentsCount > 0
                          ? `${completedAssignmentsCount}/${releasedAssignmentsCount} assignments`
                          : "Overall completion"}
                      </p>
                      <span className="text-success small fw-semibold d-flex align-items-center">
                        Details <ArrowRight2 size={12} className="ms-1" />
                      </span>
                    </div>
                  </>
                )}
              </div>
            </Link>
          </div>
          <div className="col-md-4 mb-3">
            <Link to="/organization/certificates" className="text-decoration-none text-dark">
              <div className="dash-stat-card h-100" style={{ cursor: "pointer" }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="stat-title">Certificates</span>
                  <div className="stat-icon-wrapper stat-icon-purple">
                    <Award size="22" color="#7c3aed" />
                  </div>
                </div>
                <h2 className="stat-value">{userAnalytics?.certificates_count || 0}</h2>
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <p className="stat-subtitle">Earned certificates</p>
                  <span className="text-secondary small fw-semibold d-flex align-items-center">
                    View <ArrowRight2 size={12} className="ms-1" />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-lg-8 d-flex flex-column">
            <div className="row">
              <div className="col-12 col-lg-12 mb-4">
                {/* Enrolled Courses */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h2 className="h5 fw-semibold mb-0">
                    Enrolled Courses ({enrollments.length})
                  </h2>
                  {enrollments.length > 0 && (
                    <Link to="/organization/my-courses" className="text-decoration-none small text-primary fw-medium d-flex align-items-center">
                      View All <ArrowRight2 size={14} className="ms-1" />
                    </Link>
                  )}
                </div>

                {isLoading && enrollments.length === 0 ? (
                  <div className="card modern-course-card mb-4">
                    <div className="card-body p-4">
                      <div className="d-lg-flex flex-row align-items-center">
                        <Skeleton width={90} height={90} className="rounded me-3" />
                        <div className="flex-grow-1">
                          <Skeleton width={180} height={20} className="mb-2" />
                          <Skeleton width={220} height={10} className="mb-2" />
                          <Skeleton width={80} height={15} />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : enrollments.length === 0 ? (
                  <div className="card modern-course-card mb-4">
                    <div className="card-body text-center py-5">
                      <Book size={44} className="text-muted mb-2" />
                      <h3 className="h6 fw-semibold">No courses enrolled yet</h3>
                      <p className="text-muted small mb-3">Explore available cohorts to start learning.</p>
                      <Link to="/organization/cohorts" className="btn btn-primary btn-sm">
                        Browse Courses
                      </Link>
                    </div>
                  </div>
                ) : (
                  enrollments.map((enrollment, index) => {
                    const isPrivate = enrollment?.class_option === "Private Class";
                    const itemCohort = enrollment?.cohort || {};
                    const itemProgress = enrollment?.progress || 0;
                    const itemCourseTitle = isPrivate
                      ? (enrollment?.course?.title ?? itemCohort?.course?.title ?? "Private Class")
                      : (itemCohort?.name || enrollment?.course?.title || "Cohort Course");
                    const itemThumbnail = enrollment?.course?.thumbnail_path || itemCohort?.course?.thumbnail_path;
                    const isEnded = itemCohort?.end_date ? new Date(itemCohort.end_date) < new Date() : false;
                    const itemSlug = itemCohort?.slug;
                    const isSelected = selectedCohortSlug === itemSlug;
                    const itemCanJoin = isSelected ? canJoin : false;

                    const progressLink = itemCohort?.id
                      ? `/organization/progress/cohort/${itemCohort.id}/assignments`
                      : (enrollment?.enrollment_id || enrollment?.id)
                      ? `/organization/progress/enrollment/${enrollment.enrollment_id || enrollment.id}/assignments`
                      : "/organization/progress";

                    return (
                      <div
                        className="card modern-course-card mb-3"
                        key={enrollment.id || enrollment.enrollment_id || index}
                        style={isSelected && itemSlug ? { borderLeft: '4px solid #2563eb' } : {}}
                      >
                        <div className="card-body p-3 p-md-4">
                          <div className="d-lg-flex flex-row align-items-center">
                            {itemThumbnail ? (
                              <img
                                src={itemThumbnail}
                                alt={itemCourseTitle}
                                className="modern-course-thumb me-3 mb-3 mb-lg-0"
                              />
                            ) : (
                              <div
                                className="modern-course-thumb me-3 mb-3 mb-lg-0 d-flex align-items-center justify-content-center bg-light text-muted"
                              >
                                <Book size={32} color="#64748b" />
                              </div>
                            )}

                            <div className="flex-grow-1">
                              <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
                                <h3 className="h6 fw-semibold mb-0 text-dark">
                                  {itemCourseTitle}
                                </h3>
                                <div className="d-flex align-items-center gap-2">
                                  {isPrivate ? (
                                    <span className="pill-badge badge-private">
                                      Private Class {enrollment.duration ? `• ${enrollment.duration}` : ''}
                                    </span>
                                  ) : (
                                    <span className="pill-badge badge-cohort">
                                      {enrollment.class_option || itemCohort?.name || 'Cohort'}
                                    </span>
                                  )}
                                  {isEnded && (
                                    <span className="badge bg-secondary">Ended</span>
                                  )}
                                </div>
                              </div>

                              <div className="modern-progress-track my-2">
                                <div
                                  className="modern-progress-bar"
                                  role="progressbar"
                                  style={{ width: `${itemProgress}%` }}
                                  aria-valuenow={itemProgress}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                />
                              </div>

                              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
                                <span className="small text-muted fw-medium">
                                  Progress: <strong className="text-dark">{itemProgress}%</strong>
                                  {enrollment.breakdown && (
                                    <span className="ms-2 text-muted">
                                      ({enrollment.breakdown.completed_assignments || 0}/{enrollment.breakdown.released_assignments || 0} assignments)
                                    </span>
                                  )}
                                </span>

                                <div className="d-flex flex-wrap gap-2">
                                  {!isPrivate && itemSlug && (
                                    <button
                                      type="button"
                                      className={`btn-button join-class-btn ${(!itemCanJoin || isLoading) ? 'btn-disabled' : 'primary-btn'}`}
                                      disabled={isLoading || !itemCanJoin}
                                      onClick={() => {
                                        setSelectedCohortSlug(itemSlug);
                                        if (itemCanJoin) {
                                          handleJoinClass(itemSlug);
                                        }
                                      }}
                                    >
                                      {itemCanJoin ? "Join Live Class" : "Class not live yet"}
                                    </button>
                                  )}

                                  <Link
                                    to={progressLink}
                                    className="btn btn-sm btn-primary d-flex align-items-center"
                                    style={{ padding: '0.4rem 0.85rem', fontSize: '13px', borderRadius: '8px' }}
                                  >
                                    View Progress
                                  </Link>

                                  {itemSlug && (
                                    <Link
                                      to={`/cohorts/${itemSlug}/details`}
                                      className="btn btn-sm btn-outline-secondary d-flex align-items-center"
                                      style={{ padding: '0.4rem 0.85rem', fontSize: '13px', borderRadius: '8px' }}
                                    >
                                      Details
                                    </Link>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="col-12 col-lg-12 mb-4">
                {/* Assignments & Scores */}
                <div className="card dash-stat-card shadow-sm">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h2 className="h5 fw-semibold mb-0">
                      Assignments &amp; Scores
                    </h2>

                    <Link
                      to="/organization/progress"
                      className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                      style={{ padding: '0.45rem 0.9rem', fontSize: '13px', borderRadius: '8px' }}
                    >
                      <AddCircle size={18} />
                      {isLoading ? <Skeleton width={80} /> : "View Progress"}
                    </Link>
                  </div>
                  <div className="table-responsive">
                    {isLoading ? (
                      <Skeleton
                        count={4}
                        height={40}
                        style={{ marginBottom: "8px" }}
                      />
                    ) : (
                      <table className="table table-hover mb-0 align-middle">
                        <thead>
                          <tr>
                            <th scope="col" style={{ color: "#64748b", fontWeight: 600, fontSize: "13px" }}>Assignment</th>
                            <th scope="col" style={{ color: "#64748b", fontWeight: 600, fontSize: "13px" }}>Due Date</th>
                            <th scope="col" style={{ color: "#64748b", fontWeight: 600, fontSize: "13px" }}>Status</th>
                            <th scope="col" style={{ color: "#64748b", fontWeight: 600, fontSize: "13px" }}>Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userAssignments.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="text-center text-muted py-4 small">
                                No assignments found yet.
                              </td>
                            </tr>
                          ) : (
                            userAssignments.slice(0, 5).map((assignment) => {
                              const title = assignment.title || assignment.assignment?.title || assignment.description || assignment.name || "Course Assignment";
                              const statusLower = (assignment.status || "").toLowerCase();
                              const isSubmitted = statusLower === "submitted";
                              const isGraded = statusLower === "graded";
                              const badgeClass = isSubmitted || isGraded
                                ? "bg-success-subtle text-success border border-success"
                                : "bg-warning-subtle text-warning border border-warning";

                              return (
                                <tr key={assignment.id}>
                                  <td>
                                    <span className="fw-medium text-dark">{title}</span>
                                    {assignment.cohort?.course?.title && (
                                      <span className="text-muted d-block small">{assignment.cohort.course.title}</span>
                                    )}
                                  </td>
                                  <td className="text-muted small">{formatDateDMY(assignment.created_at || assignment.due_date)}</td>
                                  <td>
                                    <span className={`badge ${badgeClass}`} style={{ textTransform: "capitalize", borderRadius: "6px" }}>
                                      {assignment.status || "Pending"}
                                    </span>
                                  </td>
                                  <td className="fw-semibold">{assignment.score || "-"}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-4 d-flex flex-column">
            <div className="row">
              <div className="col-12 col-lg-12 mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h2 className="h5 fw-semibold mb-0">Course Materials</h2>
                  <Link to="/organization/recap-videos" className="text-decoration-none small text-primary fw-medium d-flex align-items-center">
                    View All <ArrowRight2 size={14} className="ms-1" />
                  </Link>
                </div>
                {recapMaterials.length === 0 && !isLoading ? (
                  <div className="card dash-stat-card mb-3">
                    <div className="card-body text-center py-4">
                      <p className="text-muted small mb-0">
                        No course materials available yet.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Show current week label if there are multiple weeks */}
                    {weeks.length > 0 && (
                      <div className="mb-2">
                        <span className="badge bg-primary px-2 py-1 rounded-pill" style={{ fontSize: "11px" }}>
                          {getWeekLabel(weeks[0])}
                        </span>
                      </div>
                    )}
                    {displayMaterials.map((material) => {
                      const resourceType = getResourceType(material.file_path, material.title);
                      return (
                        <Link
                          key={material.id}
                          to={`/organization/recap-videos/${material.slug}`}
                          className="modern-material-item"
                        >
                          <div className={`material-icon-pill ${resourceType.pillClass}`}>
                            <span>{resourceType.icon}</span>
                          </div>
                          <div className="flex-grow-1 overflow-hidden">
                            <div className="d-flex justify-content-between align-items-center">
                              <h4 className="h6 fw-semibold mb-0 text-truncate text-dark" style={{ fontSize: "14px" }}>
                                {isLoading ? (
                                  <Skeleton width={120} />
                                ) : (
                                  material.title || "Class Material"
                                )}
                              </h4>
                              <span className="badge bg-light text-secondary ms-2 small" style={{ fontSize: "11px" }}>
                                {resourceType.label}
                              </span>
                            </div>
                            <p className="text-muted small mb-0 mt-1">
                              {isLoading ? (
                                <Skeleton width={80} />
                              ) : (
                                formatDateDMY(material.created_at) || "Recent"
                              )}
                            </p>
                          </div>
                          <ArrowRight2 size={16} className="text-muted flex-shrink-0 ms-1" />
                        </Link>
                      );
                    })}
                  </>
                )}
              </div>
              <div className="col-12 col-lg-12 mb-4">
                <h2 className="h5 fw-semibold mb-3">Tech Library</h2>
                {libraryData.length === 0 && !loading ? (
                  <div className="card custom-card">
                    <div className="card-body">
                      <p className="text-muted small mb-0"> no library data available.</p>
                      {/* <img
                    src={cohortImage}
                    alt="Tech library"
                    className="img-fluid rounded mb-3"
                  />
                  <h3 className="h6 fw-medium mb-2">
                    {isLoading ? (
                      <Skeleton width={180} />
                    ) : (
                      "Explore the Tech Library"
                    )}
                  </h3>
                  <p className="text-muted small mb-3">
                    {isLoading ? (
                      <Skeleton width={220} />
                    ) : (
                      "Access a wide range of books, articles, and resources to supplement your learning."
                    )}
                  </p>
                  <Link to="/library">
                    <button className="btn btn-dark w-100" disabled={isLoading}>
                      {isLoading ? <Skeleton width={120} /> : "Explore Library"}
                    </button>
                  </Link> */}
                    </div>
                  </div>
                ) : (
                  //  recapMaterials.slice(0, 3).map((material) => (
                  libraryData.slice(0, 1).map((library) => (
                    <div className="card dash-stat-card" key={library.id || 1}>
                      <div className="card-body p-0">
                        {library.image_url && (
                          <img
                            src={library.image_url}
                            alt={library.title || "Tech library"}
                            className="img-fluid rounded mb-3 w-100"
                            style={{ maxHeight: "160px", objectFit: "cover" }}
                          />
                        )}
                        <h3 className="h6 fw-semibold mb-2 text-dark">
                          {isLoading ? (<Skeleton width={180} />) : (
                            library.title
                          )}
                        </h3>
                        <p className="text-muted small mb-3">
                          {isLoading ? (
                            <Skeleton width={220} />
                          ) : (
                            library.description?.slice(0, 120) + "..."
                          )}
                        </p>
                        <Link to="/organization/library">
                          <button className="btn btn-primary w-100" style={{ borderRadius: "8px" }} disabled={isLoading}>
                            {isLoading ? <Skeleton width={120} /> : "Explore Library"}
                          </button>
                        </Link>
                      </div>
                    </div>
                  ))
                )}
                {/* <div className="card custom-card">
              <div className="card-body">
                {isLoading ? (
                  <Skeleton
                    width={250}
                    height={150}
                    style={{ marginBottom: "12px" }}
                  />
                ) : (
                  <img
                    src={cohortImage}
                    alt="Tech library"
                    className="img-fluid rounded mb-3"
                  />
                )}
                <h3 className="h6 fw-medium mb-2">
                  {isLoading ? (
                    <Skeleton width={180} />
                  ) : (
                    "Explore the Tech Library"
                  )}
                </h3>
                <p className="text-muted small mb-3">
                  {isLoading ? (
                    <Skeleton width={220} />
                  ) : (
                    "Access a wide range of books, articles, and resources to supplement your learning."
                  )}
                </p>
                <Link to="/library">
                  <button className="btn btn-dark w-100" disabled={isLoading}>
                    {isLoading ? <Skeleton width={120} /> : "Explore Library"}
                  </button>
                </Link>
              </div>
            </div> */}
              </div>
            </div>
          </div>
        </div>




        <FileInput isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>
    </div>
  );
}

export default MemberOrganizationDashboard;